import TextField from '@mui/material/TextField';
import { styled as muiStyled, css, useTheme } from '@mui/material/styles';
import React, { useEffect } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { useSnackbarContext } from '../base/snackbar';
import { ProjectData } from '../project-wizard/new-project-wizard-dialog';

import { Arranger } from '~/client/components/base/arranger';
import InviteMemberEntry from '~/client/components/project-wizard/invite-member-field';
import {
  useAddProjectMembersMutation,
  useEditProjectMemberMutation,
  useProjectFromIdQuery,
} from '~/graphql/generated/client';
import * as GQL from '~/graphql/generated/client';
import { updateQueryCache } from '~/graphql/util/update-cache';
import Button from '~c/components/base/button';
import Spacer, { spc } from '~c/components/base/spacer';
import Text from '~c/components/base/text';

interface Props {
  className?: string;
  onClose: () => void;
}

interface EditMemberFormProps extends Props {
  member: any;
}

const AddMembersForm: React.FC<Props> = ({ className, onClose }) => {
  const { projectId } = useParams();
  const [addProjectMembers, { loading }] = useAddProjectMembersMutation();
  const { handleSubmit, reset } = useFormContext<ProjectData>();
  const Snackbar = useSnackbarContext();
  const { fields, append, remove } = useFieldArray({
    name: 'members',
  });

  // Fetch project to get organizationId
  const { data: projectData } = useProjectFromIdQuery({
    variables: { id: projectId ?? '' },
    skip: !projectId,
    // Prevent network calls after initial load - use cache only
    fetchPolicy: 'cache-first',
  });

  const onSubmit = (data_: ProjectData) => {
    const { members } = data_;
    const organizationId = projectData?.projectFromId?.orgId;

    if (!organizationId) {
      Snackbar.open('Unable to determine organization. Please try again.', {
        showOk: true,
      });
      return;
    }

    const existingMembers = projectData?.projectFromId?.sharedWith ?? [];
    const duplicates = members?.filter((m) => {
      if (!m?.email || !m?.role) {
        return false;
      }
      return existingMembers.some(
        (existing) =>
          existing?.email?.toLowerCase() === m.email?.toLowerCase() &&
          existing?.role?.toLowerCase() === m.role?.toLowerCase()
      );
    });
    if (duplicates && duplicates.length > 0) {
      const dupList = duplicates
        .map((d) => `${d?.email} (${d?.role})`)
        .join(', ');
      Snackbar.open(
        `The following stakeholder(s) already exist with the same role: ${dupList}`,
        { showOk: true }
      );
      return;
    }

    reset();
    addProjectMembers({
      variables: {
        input: {
          projectId: projectId ?? '',
          organizationId,
          members,
        },
      },
      update: (store, { data }) => {
        onClose();
        if (!data) {
          return;
        }
        const response = data.addProjectMembers;
        Snackbar.open(
          `${
            members.length ? `Members` : `Member`
          } successfully added to project.`,
          {
            showOk: true,
          }
        );
        updateQueryCache<GQL.ProjectListQuery, GQL.ProjectListQueryVariables>(
          store,
          { query: GQL.ProjectListDocument },
          (draft) => {
            if (response.project) {
              draft.projectList.push(response.project);
            }
          }
        );
      },
      onError: (error) => {
        Snackbar.open(error.message, {
          showOk: true,
        });
      },
    });
  };

  useEffect(() => {
    if (fields.length === 0) {
      handleAddMember();
    }
  }, [fields]);

  const handleAddMember = () => {
    append({ email: '', role: 'selectrole' });
  };

  return (
    <Arranger className={className} direction="column" justify="flex-start">
      <Text sz="h1" textAlign="center">
        Invite Stakeholders
      </Text>
      <Spacer pb={spc.between_vertical_l} />
      {fields.map((field, index) => (
        <div key={field.id}>
          <div className="edit-member-entry-wrapper">
            <InviteMemberEntry
              index={index}
              removeMember={remove}
              organizationId={projectData?.projectFromId?.orgId ?? undefined}
            />
          </div>
          <Spacer pb={spc.xs} />
        </div>
      ))}
      <Spacer pb={spc.container_padding_l} />
      <Arranger direction="column">
        <Button onClick={handleAddMember} className="grow" outlined tertiary>
          Add another person
        </Button>
        <Spacer pb={spc.container_padding_l} />
        <Button
          onClick={handleSubmit(onSubmit)}
          className="grow"
          // color="success"
          loading={loading}
        >
          Looks good!
        </Button>
      </Arranger>

      <Spacer pb={spc.container_padding_l} />
    </Arranger>
  );
};

const EditMemberForm: React.FC<EditMemberFormProps> = ({
  className,
  onClose,
  member,
}) => {
  const { projectId } = useParams();
  const [addProjectMembers, { loading }] = useAddProjectMembersMutation();
  const [updateProjectMemberRole, { loading: updateRoleLoading }] =
    useEditProjectMemberMutation();
  const { handleSubmit, reset } = useFormContext<ProjectData>();
  const Snackbar = useSnackbarContext();
  const { fields, remove } = useFieldArray({
    name: 'members',
  });

  const onSubmit = (data_: ProjectData) => {
    const newRole = data_.members[0]?.role;
    updateProjectMemberRole({
      variables: {
        input: {
          projectId: projectId ?? '',
          memberId: member._id,
          role: newRole,
        },
      },
    }).then(() => {
      onClose();
      Snackbar.open('Member role updated successfully.', { showOk: true });
    });
  };

  return (
    <Arranger className={className} direction="column" justify="flex-start">
      <Text sz="h1" textAlign="center">
        Edit Contributor
      </Text>
      <Spacer pb={spc.between_vertical_l} />
      {fields.map((field, index) => (
        <div key={field.id} className="edit-member-row">
          <TextField
            label="Email"
            value={member?.email ?? ''}
            fullWidth
            disabled
            margin="normal"
            variant="outlined"
            className="email-field"
          />
          <div className="edit-member-entry-wrapper">
            <InviteMemberEntry index={index} removeMember={remove} />
          </div>
        </div>
      ))}
      <Spacer pb={spc.container_padding_l} />
      <Arranger direction="column">
        <Button
          onClick={handleSubmit(onSubmit)}
          className="grow"
          // color="success"
          loading={updateRoleLoading}
        >
          Looks good!
        </Button>
      </Arranger>
      <Spacer pb={spc.container_padding_l} />
    </Arranger>
  );
};

const StyledAddMembersForm = muiStyled(AddMembersForm)(
  ({ theme }) => css`
    display: flex;
    justify-content: center;

    /* extra large buttons */
    & .MuiButton-root {
      width: 100%;
      flex-grow: 1;
      padding: 14px 22px;
      font-size: 16px;
    }

    .edit-member-entry-wrapper {
      flex: 1;
    }
  `
);

const StyledEditMemberForm = muiStyled(EditMemberForm)(
  ({ theme }) => css`
    display: flex;
    justify-content: center;

    /* extra large buttons */
    & .MuiButton-root {
      width: 100%;
      flex-grow: 1;
      padding: 14px 22px;
      font-size: 16px;
    }

    .edit-member-entry-wrapper input[type='email'],
    .edit-member-entry-wrapper .MuiFormControl-root:first-child {
      display: none !important;
    }

    .edit-member-row {
      display: flex;
      gap: 16px;
      align-items: center;
      width: 100%;
    }

    .email-field {
      flex: 2;
    }

    .edit-member-entry-wrapper {
      flex: 1;
    }
  `
);

export default StyledAddMembersForm;
export { StyledEditMemberForm as EditMemberForm };
